import { ControlType, SETTING } from 'interfaces/settings';

// Strongly typed mapping from setting to control type
export const SETTING_TO_CONTROL_TYPE: { [K in SETTING]: ControlType } = {
    // Range settings (numbers)
    [SETTING.TIME_TO_DIAL]: ControlType.RANGE,
    [SETTING.TIME_TO_LOG]: ControlType.RANGE,
    [SETTING.AUTO_PROGRESS_TIME]: ControlType.RANGE,
    [SETTING.HANGUP_RINGING_TIME]: ControlType.RANGE,
    [SETTING.HANGUP_VOICEMAIL_TIME]: ControlType.RANGE,
    [SETTING.MAX_NUMBER_LINES]: ControlType.RANGE,
    
    // Boolean settings (toggle controls)
    [SETTING.AUTO_PROGRESS_LIVE_ACTIVE]: ControlType.TOGGLE,
    [SETTING.HANGUP_RINGING_ACTIVE]: ControlType.TOGGLE,
    [SETTING.HANGUP_VOICEMAIL_ACTIVE]: ControlType.TOGGLE,
    [SETTING.LINKEDIN_AUTO_OPEN]: ControlType.TOGGLE,
    [SETTING.COMPANY_WEBSITE_AUTO_OPEN]: ControlType.TOGGLE,
    [SETTING.SALESFORCE_URL_AUTO_OPEN]: ControlType.TOGGLE,
    [SETTING.CUSTOM_URL_AUTO_OPEN]: ControlType.TOGGLE,
    [SETTING.NO_AUTO_COMPLETE_TASK]: ControlType.TOGGLE,
    [SETTING.ACTION_ON_BAD_NUMBER]: ControlType.TOGGLE,
    [SETTING.NO_SUMMARY_AUTOMATION]: ControlType.TOGGLE,
    [SETTING.HIDE_WRONG_NUMBER_ELEMENT]: ControlType.TOGGLE,
    [SETTING.NO_LOG_ONLY]: ControlType.TOGGLE,
    [SETTING.PRIMARY_ALWAYS]: ControlType.TOGGLE,
    [SETTING.PRIMARY_FIRST]: ControlType.TOGGLE,
    [SETTING.VERIFIED_ALWAYS]: ControlType.TOGGLE,
    [SETTING.VERIFIED_FIRST]: ControlType.TOGGLE,
    [SETTING.DNC_ALWAYS]: ControlType.TOGGLE,
    [SETTING.ALL_RECORDS]: ControlType.TOGGLE,
    [SETTING.POWER_CALL_SHOULD_RING]: ControlType.TOGGLE,
    [SETTING.TEAM_CALL_MUTED]: ControlType.TOGGLE,
    [SETTING.INCOMING_CALL_MUTED]: ControlType.TOGGLE,
    [SETTING.AUTOMATIC_EXTENSION_NUMBER]: ControlType.TOGGLE,
    [SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING]: ControlType.TOGGLE,
    [SETTING.PLAY_BEEP_ON_CONNECT]: ControlType.TOGGLE,
    [SETTING.UNIVERSAL_CONTROLLER_ENABLED]: ControlType.TOGGLE,
    [SETTING.NO_NAVIGATION_ON_CONNECT]: ControlType.TOGGLE,
    [SETTING.NO_CANCEL_CALLS]: ControlType.TOGGLE,
    [SETTING.USE_NUMBER_FOR_PRIORITY_CALCULATING_TIMEZONE]: ControlType.TOGGLE,
    [SETTING.HIDE_CLICK_AUTODIALER]: ControlType.TOGGLE,
    [SETTING.HANGUP_SCREEN]: ControlType.TOGGLE,
    [SETTING.VM_NAME_MATCH_DETECTION]: ControlType.TOGGLE,
    
    // Enum settings (select controls)
    [SETTING.MUSIC_WEBSITE]: ControlType.SELECT,
    [SETTING.DIALING_MODE]: ControlType.SELECT,
    [SETTING.OUTREACH_MODAL_CLICK_SETTING]: ControlType.SELECT,
    [SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY]: ControlType.SELECT,
    
    // Map settings (custom controls)
    [SETTING.PHONE_PREFERENCE]: ControlType.CUSTOM,
    [SETTING.LOCAL_CALL_TIME_MIN]: ControlType.CUSTOM,
    [SETTING.LOCAL_CALL_TIME_MAX]: ControlType.CUSTOM,
};

// Setting display names mapping - Updated for better UX
export const SETTING_TO_DISPLAY_NAME: { [K in SETTING]: string } = {
    // System Settings - Speed
    [SETTING.TIME_TO_DIAL]: 'Time to Dial',
    [SETTING.TIME_TO_LOG]: 'Time to Log',
    
    // System Settings - Links
    [SETTING.LINKEDIN_AUTO_OPEN]: 'Auto Open LinkedIn',
    [SETTING.COMPANY_WEBSITE_AUTO_OPEN]: 'Auto Open Company Website',
    [SETTING.SALESFORCE_URL_AUTO_OPEN]: 'Auto Open Salesforce',
    [SETTING.CUSTOM_URL_AUTO_OPEN]: 'Auto Open Custom URL',

    // System Settings - Music
    [SETTING.MUSIC_WEBSITE]: 'Music Platform',
    
    // Who to Call - Number Preferences
    [SETTING.PHONE_PREFERENCE]: 'Phone Number Priority',
    [SETTING.DIALING_MODE]: 'Dialing Mode',
    
    // Who to Call - Prospect Preferences
    [SETTING.LOCAL_CALL_TIME_MIN]: 'Prospect Local Call Time Window',
    [SETTING.LOCAL_CALL_TIME_MAX]: 'Prospect Local Call Time Window',
    
    // When to Hangup
    [SETTING.HANGUP_RINGING_ACTIVE]: 'Hangup on Ringing',
    [SETTING.HANGUP_RINGING_TIME]: 'Ringing Timeout',
    [SETTING.HANGUP_VOICEMAIL_ACTIVE]: 'Hangup on Voicemail',
    [SETTING.HANGUP_VOICEMAIL_TIME]: 'Voicemail Timeout',
    
    // What to Log
    [SETTING.NO_AUTO_COMPLETE_TASK]: 'Do not Auto-Complete Tasks',
    [SETTING.NO_LOG_ONLY]: 'Do not log-only calls',
    [SETTING.NO_SUMMARY_AUTOMATION]: 'Do not add AI Summary',
    [SETTING.AUTO_PROGRESS_LIVE_ACTIVE]: 'Auto Progress for Quick Conversations',
    [SETTING.AUTO_PROGRESS_TIME]: 'Max Time for Quick Conversations',
    [SETTING.ACTION_ON_BAD_NUMBER]: 'Remove/label bad numbers on detection',
    [SETTING.HIDE_WRONG_NUMBER_ELEMENT]: 'Hide remove number UI',
    
    // Automations
    [SETTING.PLAY_BEEP_ON_CONNECT]: 'Play beep on connect',
    [SETTING.NO_NAVIGATION_ON_CONNECT]: 'Do not navigate to contact on connect',
    [SETTING.NO_CANCEL_CALLS]: 'Do not cancel calls',
    [SETTING.UNIVERSAL_CONTROLLER_ENABLED]: 'Enable Universal Controller',
    [SETTING.INCOMING_CALL_MUTED]: 'Mute Incoming Calls',
    [SETTING.TEAM_CALL_MUTED]: 'Mute Team Calls',
    [SETTING.AUTOMATIC_EXTENSION_NUMBER]: 'Auto-Dial Extension Numbers',
    [SETTING.ADD_TRANSCRIPT_TO_SUMMARY_SETTING]: 'Add Transcript URL to Summary',
    [SETTING.MAX_NUMBER_LINES]: 'Max Parallel Lines (only for parallel users)',
    [SETTING.OUTREACH_MODAL_CLICK_SETTING]: 'Outreach Recording Setting',
    
    [SETTING.PRIMARY_ALWAYS]: 'Always dial primary numbers',
    [SETTING.PRIMARY_FIRST]: 'Dial primary numbers first',
    [SETTING.VERIFIED_ALWAYS]: 'Only dial verified numbers',
    [SETTING.VERIFIED_FIRST]: 'Dial verified numbers first',
    [SETTING.DNC_ALWAYS]: 'Do not dial DNC numbers',
    [SETTING.ALL_RECORDS]: 'Dial account #s',
    [SETTING.POWER_CALL_SHOULD_RING]: 'Play ringing when power dialing',
    [SETTING.DEFAULT_NUMBER_ROTATION_STRATEGY]: 'Default Number Rotation Strategy',
    [SETTING.USE_NUMBER_FOR_PRIORITY_CALCULATING_TIMEZONE]: 'Prioritize Number for Timezone Calculation',
    [SETTING.HIDE_CLICK_AUTODIALER]: 'Hide Click Autodialer',
    [SETTING.HANGUP_SCREEN]: 'Automatically Hangup on iOS/Android Screeners',
    [SETTING.VM_NAME_MATCH_DETECTION]: 'Detect Voicemail by Matching Contact Name',
}; 